import { Injectable, signal, inject } from '@angular/core';
import { PercentPipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CustomFiltersService {
  private percPipe = inject(PercentPipe);
  private decPipe = inject(DecimalPipe);
  private currPipe = inject(CurrencyPipe);

  private updatePresets = new Subject();
  private updateValueRange = new Subject();
  private updateDropdowns = new Subject();
  private updateSolution = new Subject();
  private updateRepReport = new Subject();
  private reloadFilters = new Subject();
  private updateGroupBy = new Subject();
  private updateProgram = new Subject();
  private updateConditionalFilter = new Subject();
  updatePresets$ = this.updatePresets.asObservable();
  updateValueRange$ = this.updateValueRange.asObservable();
  updateDropdowns$ = this.updateDropdowns.asObservable();
  updateSolution$ = this.updateSolution.asObservable();
  updateRepReport$ = this.updateRepReport.asObservable();
  reloadFilters$ = this.reloadFilters.asObservable();
  updateGroupBy$ = this.updateGroupBy.asObservable();
  updateProgram$ = this.updateProgram.asObservable();
  updateConditionalFilter$ = this.updateConditionalFilter.asObservable();

  hashingStatus = signal(false);

  reloadFilterValues(value) {
    this.reloadFilters.next(value);
  }

  updatePresetDateValues(values) {
    this.updatePresets.next(values);
  }

  updateDropdownValues(values, varName) {
    this.updateDropdowns.next({ values, varName });
  }

  updateValueRanges(values) {
    this.updateValueRange.next(values);
  }

  updateSolutionSelection(value) {
    this.updateSolution.next(value);
  }

  updateRepReportSelection(value) {
    this.updateRepReport.next(value);
  }

  updateGroupByValue(value) {
    this.updateGroupBy.next(value);
  }

  updateProgramValue(value) {
    this.updateProgram.next(value);
  }

  updateConditionalValue(values, varName) {
    this.updateConditionalFilter.next({ values, varName });
  }

  buildColumnFilterOptions(gridData, headers) {
    const output = {};
    const keys = gridData && gridData[0] ? Object.keys(gridData[0]) : [];

    keys.forEach(key => {
      let col = gridData.map(row => row[key]);
      col = [...new Set(col)];
      col = col.sort((a, b) => (a < b ? -1 : 1));
      col = col.map(x => {
        if (x || (!x && (typeof x === 'boolean' || x === 0))) {
          let displayValue;
          const header = headers.find(z => z.DataField === key);
          if (header && header.Type) {
            switch (header.Type) {
              case 'perc':
                if (x === -9.99 || x === -999) {
                  displayValue = 'N/A';
                  x = 'N/A';
                } else if (x === 'N/A' || x === 'Uncapped') {
                  displayValue = x;
                } else {
                  displayValue = this.percPipe.transform(x);
                }
                break;

              case 'perc2':
                if (x === -9.99 || x === -999) {
                  displayValue = 'N/A';
                  x = 'N/A';
                } else if (x === 'N/A' || x === 'Uncapped') {
                  displayValue = x;
                } else {
                  displayValue = this.percPipe.transform(x, '1.2');
                }
                break;

              case 'perc3':
                if (x === -9.99 || x === -999) {
                  displayValue = 'N/A';
                  x = 'N/A';
                } else if (x === 'N/A' || x === 'Uncapped') {
                  displayValue = x;
                } else {
                  displayValue = this.percPipe.transform(x, '1.3');
                }
                break;

              case 'integer':
                displayValue = this.decPipe.transform(x, '1.0-0');
                break;

              case 'curr':
                displayValue = this.currPipe.transform(x);
                break;

              case 'rateLock':
                if (x.toLowerCase() === 'f') {
                  displayValue = 'Full Term';
                } else if (x.toLowerCase() === 'a') {
                  displayValue = 'Annual';
                } else if (x.toLowerCase() === 's') {
                  displayValue = 'Semi-annual';
                } else if (x.toLowerCase() === 'q') {
                  displayValue = 'Quarterly';
                } else if (x.toLowerCase() === 'm') {
                  displayValue = 'Monthly';
                } else if (x.toLowerCase() === 'd') {
                  displayValue = 'Daily';
                }
                break;

              case 'bool':
                if (x) {
                  displayValue = 'Yes';
                } else if (!x && typeof x === 'boolean') {
                  displayValue = 'No';
                }
                break;

              default:
                displayValue = x;
                break;
            }
          } else {
            displayValue = x;
          }

          return { display: displayValue, value: x };
        }
      });
      col = col.filter(x => {
        if (
          (x && x.value) ||
          (x && !x.value && (typeof x.value === 'boolean' || x.value === 0))
        ) {
          return true;
        }
      });

      output[key] = col;
    });
    return output;
  }

  deDupObjectArray(data) {
    const uniqueArray = data.filter(
      (object, index) =>
        index ===
        data.findIndex(obj => JSON.stringify(obj) === JSON.stringify(object))
    );
    return uniqueArray;
  }

  calculatePresetRange(filter, queryData, returnRange = false) {
    let calculatedStartDate;

    const formatStartDate = () =>
      DateTime.fromJSDate(new Date(calculatedStartDate)).toUTC().toISO();

    const endDate = DateTime.fromJSDate(new Date()).toUTC().toISO();

    if (filter['value'] === 'mtd') {
      // Set the date picker with the selected times
      calculatedStartDate = DateTime.now().startOf('month').toLocaleString();
      if (returnRange) {
        return { start: formatStartDate(), end: endDate };
      }
      queryData[filter['field']]['start'] = formatStartDate();
      queryData[filter['field']]['end'] = endDate;
    } else if (filter['value'] === 'ytd') {
      // Set the date picker with the selected times
      calculatedStartDate = DateTime.now().startOf('year').toLocaleString();
      if (returnRange) {
        return { start: formatStartDate(), end: endDate };
      }
      queryData[filter['field']]['start'] = formatStartDate();
      queryData[filter['field']]['end'] = endDate;
    } else if (filter['value'] === '12mtd') {
      // Set the date picker with the selected times
      calculatedStartDate = DateTime.now()
        .minus({ years: 1 })
        .startOf('month')
        .toLocaleString();
      if (returnRange) {
        return { start: formatStartDate(), end: endDate };
      }
      queryData[filter['field']]['start'] = formatStartDate();
      queryData[filter['field']]['end'] = endDate;
    } else if (
      filter['value'] === '7 days' ||
      filter['value'] === '30 days' ||
      filter['value'] === '90 days'
    ) {
      // Calculate time difference
      const calculateTime = filter['value'].split(' ')[0];
      const calculateUnit = filter['value'].split(' ')[1];
      const timeDiffObject = {};
      timeDiffObject[calculateUnit] = calculateTime;
      calculatedStartDate = DateTime.now()
        .minus(timeDiffObject)
        .toLocaleString();

      // Set the date picker with the selected times
      if (returnRange) {
        return { start: formatStartDate(), end: endDate };
      }
      queryData[filter['field']]['start'] = formatStartDate();
      queryData[filter['field']]['end'] = endDate;
    } else if (filter['value'] === 'custom' && filter['customRange']) {
      if (returnRange) {
        return {
          start: filter['customRange']['start'],
          end: filter['customRange']['end'],
        };
      }
      queryData[filter['field']]['start'] = filter['customRange']['start'];
      queryData[filter['field']]['end'] = filter['customRange']['end'];
      this.updatePresetDateValues(filter);
    }
  }

  calculatePresetSelection(start, end) {
    const selectedStart = DateTime.fromISO(start, {
      zone: 'utc',
    });
    const selectedEnd = DateTime.fromISO(end, {
      zone: 'utc',
    });
    // if end is unparsable, it is not a custom range
    if (end && selectedEnd?.invalid?.reason === 'unparsable') {
      return end;
    } else if (start && selectedStart?.invalid?.reason === 'unparsable') {
      return start;
    }
    // If the end date matches today then it's possible to be a preset
    if (
      selectedEnd.toLocaleString() ===
      DateTime.now().endOf('day').toUTC().toLocaleString()
    ) {
      const { days } = selectedEnd.diff(selectedStart, 'days').toObject();
      if (
        selectedEnd.startOf('month').toLocaleString() ===
          selectedStart.toLocaleString() ||
        start.split('T')[0] ===
          selectedEnd.startOf('month').toISO().split('T')[0]
      ) {
        return 'mtd';
      } else if (
        selectedEnd.startOf('year').toLocaleString() ===
          selectedStart.toLocaleString() ||
        start.split('T')[0] ===
          selectedEnd.startOf('year').toISO().split('T')[0]
      ) {
        return 'ytd';
      } else if (
        selectedEnd.minus({ years: 1 }).startOf('month').toLocaleString() ===
          selectedStart.toLocaleString() ||
        start.split('T')[0] ===
          selectedEnd.minus({ years: 1 }).startOf('month').toISO().split('T')[0]
      ) {
        return '12mtd';
      } else {
        switch (Math.trunc(days)) {
          case 7:
            return '7 days';
          case 30:
            return '30 days';
          case 90:
            return '90 days';
          case 91:
            return '90 days';
          default:
            return 'custom';
        }
      }
    } else {
      return 'custom';
    }
  }
}
